import { useLayoutEffect } from 'react';

/**
 * Um único IntersectionObserver para a página inteira.
 *
 * Observa tudo que tiver a classe `.reveal`, adiciona `.is-visible` quando
 * entra em cena e para de observar o elemento — reanimar a cada scroll irrita.
 *
 * O estado inicial (opacity: 0) só é aplicado depois que este hook marca
 * <html class="motion-ready">. Se o JS não rodar, o conteúdo nasce visível.
 *
 * useLayoutEffect, e não useEffect: a classe precisa entrar antes do primeiro
 * paint. Com useEffect o conteúdo aparecia, sumia e só então revelava.
 */
export function useReveal(): void {
  useLayoutEffect(() => {
    const root = document.documentElement;

    // matchMedia não existe em jsdom nem em navegadores muito antigos;
    // sem ele, assume-se que não há preferência por movimento reduzido.
    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      return;
    }

    root.classList.add('motion-ready');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    const observeAll = () => {
      document
        .querySelectorAll('.reveal:not(.is-visible)')
        .forEach((el) => observer.observe(el));
    };

    observeAll();

    // Rotas trocam de conteúdo sem remontar o App, então novos alvos precisam
    // ser recolhidos. Coalescido num frame: sem isso, cada mutação dispararia
    // um querySelectorAll no documento inteiro.
    let queued = 0;
    const mutations = new MutationObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        observeAll();
      });
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (queued) cancelAnimationFrame(queued);
      observer.disconnect();
      mutations.disconnect();
      root.classList.remove('motion-ready');
    };
  }, []);
}

export default useReveal;
